import { useEffect, useRef, useState } from "react";
import type { PhotoStoryConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { SkipButton } from "../ui/SkipButton";

/**
 * "The Influencer" arc (item i) — one component, two presentations of the
 * SAME photo set:
 *   mode "post" -> the stranger composes + posts a social story: her caption
 *                  is revealed line by line, camera-shutter SFX plays, then
 *                  the photos land one after another.
 *   mode "feed" -> the next day, Tom taps an Instagram-style app icon and
 *                  browses the very same photos in a simple phone feed
 *                  (mirrors `CocktailQrPhone`'s phone-chrome convention).
 * Full-viewport overlay either way — no 3D asset/screenQuad needed for this
 * beat (it happens beside the bar stool / on Tom's own phone, not on a
 * hacker-room monitor).
 */
export function PhotoStory({ config, onDone }: { config: PhotoStoryConfig; onDone: () => void }) {
  return config.mode === "feed" ? <FeedStage config={config} onDone={onDone} /> : <PostStage config={config} onDone={onDone} />;
}

function PostStage({ config, onDone }: { config: PhotoStoryConfig; onDone: () => void }) {
  const [captionShown, setCaptionShown] = useState(0);
  const [shutters, setShutters] = useState(0);
  const [photosShown, setPhotosShown] = useState(0);
  const [ready, setReady] = useState(false);
  const started = useRef(false);
  const mounted = useRef(true);
  const skipped = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    for (const t of config.captionLines ?? []) {
      void audio.prefetchSpeech(t, {
        voice: config.voiceId ?? "nova",
        lang: config.language ?? "en-US",
        instructions: "Bright, upbeat, a little performative — narrating her own social post out loud as she types it.",
      });
    }
    if (started.current) return;
    started.current = true;
    const lines = config.captionLines ?? [];
    const photos = config.photos ?? [];
    let cancelled = false;
    (async () => {
      if (config.narratorLine) {
        await audio.speak(config.narratorLine, { voice: "onyx", lang: config.language ?? "en-US" });
        if (cancelled || skipped.current || !mounted.current) return;
        await new Promise((r) => window.setTimeout(r, 400));
      }
      for (let i = 0; i < lines.length; i++) {
        if (cancelled || skipped.current || !mounted.current) return;
        setCaptionShown(i + 1);
        await audio.speak(lines[i], {
          voice: config.voiceId ?? "nova",
          lang: config.language ?? "en-US",
          instructions: "Bright, upbeat, a little performative — narrating her own social post out loud as she types it.",
        });
        await new Promise((r) => window.setTimeout(r, 250));
      }
      for (let i = 0; i < photos.length; i++) {
        if (cancelled || skipped.current || !mounted.current) return;
        audio.shutter();
        setShutters((n) => n + 1);
        await new Promise((r) => window.setTimeout(r, 550));
        if (cancelled || skipped.current || !mounted.current) return;
        setPhotosShown(i + 1);
        await new Promise((r) => window.setTimeout(r, 500));
      }
      if (cancelled || skipped.current || !mounted.current) return;
      setReady(true);
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    if (skipped.current) return;
    skipped.current = true;
    audio.cancelSpeech();
    setCaptionShown((config.captionLines ?? []).length);
    setPhotosShown((config.photos ?? []).length);
    setShutters((config.photos ?? []).length);
    setReady(true);
  };

  return (
    <div className="pstory-wrap" role="dialog" aria-label="Posting a story">
      <div className="pstory-card">
        <div className="pstory-head">
          <span className="pstory-head-dot" aria-hidden />
          Posting a story
          {shutters > 0 && shutters < (config.photos ?? []).length && (
            <span className="pstory-shutter-ico" aria-hidden>
              {"\u{1F4F8}"}
            </span>
          )}
        </div>

        {(config.captionLines ?? []).length > 0 && (
          <div className="pstory-caption">
            {(config.captionLines ?? []).slice(0, captionShown).map((l, i) => (
              <div key={i} className="pstory-caption-line">
                {l}
              </div>
            ))}
          </div>
        )}

        {photosShown > 0 && (
          <div className="pstory-photos">
            {(config.photos ?? []).slice(0, photosShown).map((p, i) => (
              <div key={i} className="pstory-photo" style={{ animationDelay: `${i * 40}ms` }}>
                <img src={p.url} alt={p.alt ?? ""} />
              </div>
            ))}
          </div>
        )}

        <div className="cq-continue-group pstory-continue-group">
          <button type="button" className="btn pstory-continue" disabled={!ready} onClick={onDone}>
            {ready ? config.continueLabel ?? "Continue" : "\u2026"}
          </button>
          {!ready && <SkipButton onClick={skip} label="Skip ahead" />}
        </div>
      </div>
    </div>
  );
}

function FeedStage({ config, onDone }: { config: PhotoStoryConfig; onDone: () => void }) {
  const [opened, setOpened] = useState(false);
  const narrated = useRef(false);
  const reflected = useRef(false);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.reflectionLine) {
      void audio.prefetchSpeech(config.reflectionLine, {
        voice: "onyx",
        lang: config.language ?? "en-US",
        instructions: "Quiet, a little uneasy — an inner second thought, not spoken aloud to anyone.",
      });
    }
    if (narrated.current || !config.narratorLine) {
      narrated.current = true;
      return;
    }
    narrated.current = true;
    void audio.speak(config.narratorLine, { voice: "onyx", lang: config.language ?? "en-US" });
    return () => audio.cancelSpeech();
  }, [config.narratorLine, config.language]);

  // The moment he actually opens the post (not on mount) — a private second
  // thought playing "at the same time" as he's looking at it, distinct from
  // the establishing narratorLine above.
  useEffect(() => {
    if (!opened || reflected.current || !config.reflectionLine) return;
    reflected.current = true;
    void audio.speak(config.reflectionLine, {
      voice: "onyx",
      lang: config.language ?? "en-US",
      instructions: "Quiet, a little uneasy — an inner second thought, not spoken aloud to anyone.",
    });
    return () => audio.cancelSpeech();
  }, [opened, config.reflectionLine, config.language]);

  return (
    <div className="pstory-wrap pstory-wrap--feed" role="dialog" aria-label="Phone">
      <div className="quish-phone">
        <div className="quish-notch" />
        <div className="quish-statusbar">
          <span>9:14 AM</span>
          <span className="quish-sb-icons">
            <span>{"\u{1F4F6}"}</span>
            <span>{"\u{1F50B}"}</span>
          </span>
        </div>
        <div className="quish-screen cqr-screen pstory-phone-screen">
          {!opened ? (
            <div className="pstory-home">
              <div className="pstory-home-hint">Tap to open</div>
              <button
                type="button"
                className="pstory-app-icon cf-pulse-target"
                onClick={() => {
                  audio.click();
                  setOpened(true);
                }}
              >
                <span className="pstory-app-glyph" aria-hidden>
                  {"\u{1F4F7}"}
                </span>
                <span className="pstory-app-label">{config.appLabel ?? "Instagram"}</span>
              </button>
            </div>
          ) : (
            <div className="pstory-feed">
              <div className="pstory-feed-head">{config.appLabel ?? "Instagram"}</div>
              <div className="pstory-feed-scroll">
                {(config.photos ?? []).map((p, i) => (
                  <div key={i} className="pstory-feed-post">
                    <img src={p.url} alt={p.alt ?? ""} />
                  </div>
                ))}
                {config.hashtags && config.hashtags.length > 0 && (
                  <div className="pstory-feed-tags">{config.hashtags.join(" ")}</div>
                )}
              </div>
              <button type="button" className="btn pstory-continue pstory-feed-continue" onClick={onDone}>
                {config.continueLabel ?? "Continue"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
